/**
 * A circle's own link.
 *
 * Every circle can be given away the way the whole ring can: `/c/<token>`
 * opens it as a ring of its own — the people in it, the circles inside it,
 * nobody else — read-only, and on its own terms. Closing the whole ring does
 * not close it; opening the ring does not open it. The controls here are the
 * same two the calendar's link has, replace and stop, and they appear in two
 * places: the circle's own disclosure in the Circles panel, and the list in the
 * Share dialog, where every circle's link can be seen side by side.
 */
import type { Tag } from '@circle/shared';
import { api } from '../app/api';
import { button, copyRow, el, type Child } from './dom';
import { confirmDialog } from './dialog';
import { toast, toastError } from './toast';
import { shareUrl } from './share-links';

export interface CircleShareOptions {
  calendarId: string;
  tag: Tag;
  /** The circle came back changed; the caller refetches and repaints. */
  onUpdated: (tag: Tag) => void | Promise<void>;
}

async function setShare(options: CircleShareOptions, enabled: boolean, rotate = false): Promise<void> {
  try {
    const tag = await api.shareTag(options.calendarId, options.tag.id, enabled, rotate);
    await options.onUpdated(tag);
    toast(
      !enabled
        ? `${options.tag.name} is no longer shared.`
        : rotate
          ? `New link for ${options.tag.name} ready.`
          : `${options.tag.name} has a link of its own.`,
    );
  } catch (err) {
    toastError(err, 'That link did not change.');
  }
}

function replaceButton(options: CircleShareOptions): HTMLElement {
  return button(
    'Replace link',
    async () => {
      const yes = await confirmDialog({
        title: `Replace the link to ${options.tag.name}?`,
        description: 'The current link stops working straight away. Anyone still using it will need the new one.',
        confirmLabel: 'Replace link',
      });
      if (yes) await setShare(options, true, true);
    },
    { class: 'btn--small btn--quiet', 'data-role': 'rotate-share' },
  );
}

function stopButton(options: CircleShareOptions): HTMLElement {
  return button(
    'Stop sharing',
    async () => {
      const yes = await confirmDialog({
        title: `Stop sharing ${options.tag.name}?`,
        description: 'The link stops opening anything. The circle itself, and the whole ring’s own link, are untouched.',
        confirmLabel: 'Stop sharing',
        danger: true,
      });
      if (yes) await setShare(options, false);
    },
    { class: 'btn--small btn--quiet', 'data-role': 'stop-share' },
  );
}

function startButton(options: CircleShareOptions, label: string): HTMLElement {
  return button(label, () => void setShare(options, true), {
    class: 'btn--small',
    'data-role': 'start-share',
  });
}

/** The section in a circle's disclosure on the Circles panel. */
export function circleShareSection(options: CircleShareOptions): HTMLElement {
  const token = options.tag.shareToken;
  const body: Child[] = token
    ? [
        copyRow(shareUrl(token), 'Copy link'),
        el('div', { class: 'row circle__share-actions' }, replaceButton(options), stopButton(options)),
      ]
    : [el('div', { class: 'row' }, startButton(options, 'Share this circle as a link'))];
  return el(
    'div',
    { class: 'field circle__share', 'data-shared': token ? 'true' : 'false' },
    el('span', { class: 'small muted', text: 'Its own link' }),
    ...body,
    el('div', {
      class: 'field__hint',
      text: 'Opens this circle as a ring of its own: the people in it and the circles inside it, nobody else, read-only. It works whether or not the whole ring is shared.',
    }),
  );
}

/** One row of the Share dialog's list: the name, then the link or the way to get one. */
export function circleShareRow(options: CircleShareOptions & { depth: number }): HTMLElement {
  const token = options.tag.shareToken;
  const row = el('li', {
    class: 'share-circles__row',
    'data-circle': options.tag.id,
    'data-shared': token ? 'true' : 'false',
  });
  row.style.setProperty('--depth', String(options.depth));
  row.append(
    el('span', { class: 'share-circles__name', text: options.tag.name }),
    token
      ? el('div', { class: 'share-circles__link' }, copyRow(shareUrl(token), 'Copy'), replaceButton(options), stopButton(options))
      : startButton(options, 'Get a link'),
  );
  return row;
}
